import { FC, Fragment } from 'react';
import { twMerge } from 'tailwind-merge';

import { ChevronDownIcon, InfoCircle } from '../../../../../assets/icons/index';
import { Tooltip } from '../../../../../components/tooltip/index';
import { useLocale } from '../../../../../hooks/index';
import { toMathRound } from '../../../../../utils/to-math-round.util';
import { toNumber } from '../../../../../utils/to-number.util';
import {
  formatScenarioCapTableCellValue,
  isCapTableEmptyDisplayValue,
} from '../../format-scenario-cap-table-cell-value.util';
import {
  CapTableTab,
  Column,
  ColumnVariant,
  ShareClassColumns,
  shareClassesViewByColumns,
  StakeholderColumns,
  StakeholdersViewBy,
  stakeholdersViewByColumns,
} from '../liquidity.types';

export type FirstTableRowPayload = {
  change: null | number | number[] | string | string[];
  value: number | number[] | string | string[];
};

export type SecondTableRowPayload = {
  cap: number;
  investment: number;
  investmentTime: number;
  multiple: number;
}[];

type TableRowProps = {
  className?: string;
  handleExpandRow?: React.Dispatch<React.SetStateAction<boolean>>;
  isExpanded?: boolean;
  isExpandedRow: boolean;
  isPool?: boolean;
  selectedViewBy?: 'all' | StakeholdersViewBy;
  showToolTip?: boolean;
  tdClassName?: string;
  trClassName?: string;
  viewBy?: Record<StakeholdersViewBy, boolean>;
  visibleColumns: Column[];
} & (
  | {
      fields: Record<ShareClassColumns, FirstTableRowPayload | null>;
      tab: CapTableTab.BY_SHARE_CLASS;
    }
  | {
      fields: Record<StakeholderColumns, FirstTableRowPayload | null | SecondTableRowPayload>;
      tab: CapTableTab.BY_STAKEHOLDER;
    }
);
const allColumnsMap = Object.values(Column);

const allColumnFields = allColumnsMap.reduce(
  (acc, curr) => ({ ...acc, [curr]: null }),
  {},
) as Record<Column, null>;

export const TableRow: FC<TableRowProps> = ({
  fields,
  className,
  trClassName,
  visibleColumns = [],
  viewBy,
  handleExpandRow,
  isExpanded,
  isExpandedRow,
  tab,
  selectedViewBy = 'all',
  tdClassName,
  showToolTip,
  isPool = false,
}) => {
  const { messagesLocale } = useLocale();
  const viewByAll = selectedViewBy === 'all';

  const basicIsVisibleChecks = allColumnsMap.reduce(
    (acc, curr) => ({ ...acc, [curr]: visibleColumns.includes(curr) }),
    {},
  ) as Record<Column, boolean>;

  const isVisibleChecks: Record<ColumnVariant, boolean> = {
    ...basicIsVisibleChecks,
    [Column.NAME]: visibleColumns.includes(Column.NAME) && viewByAll,
    [Column.STAKEHOLDER_NAME]:
      visibleColumns.includes(Column.STAKEHOLDER_NAME) &&
      !viewByAll &&
      (viewBy?.stakeholders || false),
    [Column.TYPE]: visibleColumns.includes(Column.TYPE) && (!viewByAll || isPool),
  };
  const fieldsMap =
    tab === CapTableTab.BY_SHARE_CLASS
      ? shareClassesViewByColumns
      : stakeholdersViewByColumns[selectedViewBy];

  const allFields = { ...allColumnFields, ...fields };

  return (
    <>
      <tr className={twMerge(isExpanded && 'shadow-[inset_2px_0px_0px_0px_#2565C8]', trClassName)}>
        {viewByAll || isPool ? (
          <td
            className={twMerge(
              'w-12 text-center shadow-[inset_0px_-1px_0px_0px_#F2F4F7]',
              !isExpandedRow && 'cursor-pointer',
              isExpanded && 'bg-brand-50 shadow-[inset_2px_0px_0px_0px_#2565C8]',
              className,
            )}
            onClick={() => !isExpandedRow && handleExpandRow?.((prev) => !prev)}
          >
            <span className="centered flex w-12 shrink-0 text-sm font-[450] text-gray-700">
              {!isExpandedRow && (
                <ChevronDownIcon
                  className={twMerge('transition-all', isExpanded && 'rotate-180')}
                  iconColor={isExpanded ? '#2565C8' : '#344054'}
                />
              )}
            </span>
          </td>
        ) : (
          <td
            className={twMerge(
              'w-12 text-center shadow-[inset_0px_-1px_0px_0px_#F2F4F7]',
              !isExpandedRow && 'cursor-pointer',
              isExpanded && 'bg-brand-50 shadow-[inset_2px_0px_0px_0px_#2565C8]',
              className,
            )}
          ></td>
        )}
        {fieldsMap.map((field, i) => {
          if (!isVisibleChecks[field]) return null;

          const rowPayload = allFields[field];

          if (!Array.isArray(rowPayload)) {
            const payloadValue = rowPayload?.value;
            const payloadChange = rowPayload?.change;

            if (Array.isArray(payloadValue)) {
              return payloadValue?.map((filed, i) => (
                <td
                  className="px-4 py-6 shadow-[inset_0px_-1px_0px_0px_#F2F4F7]"
                  key={`${field}_${i}_${tab}`}
                >
                  <div className="flex max-w-[360px] gap-6 overflow-hidden">
                    <span
                      className={twMerge(
                        'w-[80px] text-sm font-[450] text-gray-700',
                        (!allFields[field] ||
                          isCapTableEmptyDisplayValue(
                            formatScenarioCapTableCellValue(field, filed),
                          )) &&
                          'text-gray-300',
                        tdClassName,
                      )}
                    >
                      {formatScenarioCapTableCellValue(field, filed)}
                    </span>
                    {Array.isArray(payloadChange) && payloadChange[i] !== null ? (
                      <span
                        className={twMerge(
                          'w-[80px] rounded-2xl px-[6px] py-[2px] text-center text-xs font-medium',
                          typeof payloadChange[i] === 'string' && 'text-gray-400',
                          typeof payloadChange[i] === 'number' &&
                            (payloadChange[i] > 0
                              ? 'bg-forest-50 text-forest-600'
                              : payloadChange[i] < 0
                                ? 'bg-fireside-50 text-fireside-600'
                                : 'bg-gray-50 text-gray-400'),
                        )}
                      >
                        {typeof payloadChange[i] === 'number'
                          ? payloadChange[i] > 0
                            ? `+${toMathRound(toNumber(payloadChange[i]) * 100).toLocaleString('en-US')}%`
                            : payloadChange[i] < 0
                              ? `${toMathRound(toNumber(payloadChange[i]) * 100)}%`
                              : `00`
                          : payloadChange[i]}
                      </span>
                    ) : (
                      <>
                        {field !== Column.TYPE &&
                          field !== Column.NAME &&
                          field !== Column.STAKEHOLDER_NAME && <span className="w-[80px]" />}
                      </>
                    )}
                  </div>
                </td>
              ));
            }

            return (
              <td
                className="px-4 py-6 shadow-[inset_0px_-1px_0px_0px_#F2F4F7]"
                key={`${field}_${i}_${tab}`}
              >
                <div
                  className={twMerge(
                    'flex max-w-[360px] items-center overflow-hidden',
                    showToolTip && (field === Column.NAME || field === Column.STAKEHOLDER_NAME)
                      ? 'gap-4'
                      : 'gap-6',
                  )}
                >
                  <span
                    className={twMerge(
                      'truncate text-ellipsis whitespace-nowrap text-sm font-[450] text-gray-700',
                      isCapTableEmptyDisplayValue(
                        formatScenarioCapTableCellValue(field, payloadValue),
                      ) && 'w-[80px] text-gray-300',
                      (typeof payloadValue === 'number' || !isNaN(Number(payloadValue))) &&
                        'w-[80px]',
                      tdClassName,
                    )}
                  >
                    {formatScenarioCapTableCellValue(field, payloadValue)}
                  </span>
                  {showToolTip && (field === Column.NAME || field === Column.STAKEHOLDER_NAME) && (
                    <Tooltip content={messagesLocale['ANTI_DILUTION_TOOLTIP']}>
                      <InfoCircle className="size-4 cursor-pointer" iconColor="#344054" />
                    </Tooltip>
                  )}
                  {payloadChange !== null && field !== Column.AVERAGE_SHARE_PRICE ? (
                    <span
                      className={twMerge(
                        'flex w-[80px] justify-center rounded-2xl px-[6px] py-[2px] text-center text-xs font-medium',
                        typeof payloadChange === 'string' && 'text-gray-400',
                        typeof payloadChange === 'number' &&
                          (payloadChange > 0
                            ? 'bg-forest-50 text-forest-600'
                            : payloadChange < 0
                              ? 'bg-fireside-50 text-fireside-600'
                              : 'bg-gray-50 text-gray-400'),
                      )}
                    >
                      {typeof payloadChange === 'number'
                        ? payloadChange > 0
                          ? `+${toMathRound(toNumber(payloadChange) * 100).toLocaleString('en-US')}%`
                          : payloadChange < 0
                            ? `${toMathRound(toNumber(payloadChange) * 100)}%`
                            : `00`
                        : payloadChange}
                    </span>
                  ) : (
                    <>
                      {field !== Column.TYPE &&
                        field !== Column.NAME &&
                        field !== Column.STAKEHOLDER_NAME && <span className="w-[80px]" />}
                    </>
                  )}
                </div>
              </td>
            );
          }

          return rowPayload?.map(({ cap, investment, investmentTime, multiple }, i) => (
            <Fragment key={`${field}_${i}_${tab}`}>
              {isVisibleChecks[Column.INVESTMENT] && (
                <td className="px-4 py-6 shadow-[inset_0px_-1px_0px_0px_#F2F4F7]">
                  <div className="flex max-w-[360px] gap-6 overflow-hidden">
                    <span
                      className={twMerge(
                        'w-[80px] text-sm font-[450] text-gray-700',
                        !investment && 'text-gray-300',
                        tdClassName,
                      )}
                    >
                      {toMathRound(investment)?.toLocaleString('en-US')}
                    </span>
                  </div>
                </td>
              )}
              {isVisibleChecks[Column.MULTIPLE] && (
                <td className="px-4 py-6 shadow-[inset_0px_-1px_0px_0px_#F2F4F7]">
                  <div className="flex max-w-[360px] gap-6 overflow-hidden">
                    <span
                      className={twMerge(
                        'w-[80px] text-sm font-[450] text-gray-700',
                        !multiple && 'text-gray-300',
                        tdClassName,
                      )}
                    >
                      {toMathRound(multiple)?.toLocaleString('en-US')}
                    </span>
                  </div>
                </td>
              )}
              {isVisibleChecks[Column.INVESTMENT_TIME] && (
                <td className="px-4 py-6 shadow-[inset_0px_-1px_0px_0px_#F2F4F7]">
                  <div className="flex max-w-[360px] gap-6 overflow-hidden">
                    <span
                      className={twMerge(
                        'w-[80px] text-sm font-[450] text-gray-700',
                        !investmentTime && 'text-gray-300',
                        tdClassName,
                      )}
                    >
                      {toMathRound(investmentTime)?.toLocaleString('en-US')}
                    </span>
                  </div>
                </td>
              )}
              {isVisibleChecks[Column.CAP_PARTICIPATING] && (
                <td className="px-4 py-6 shadow-[inset_0px_-1px_0px_0px_#F2F4F7]">
                  <div className="flex max-w-[360px] gap-6 overflow-hidden">
                    <span
                      className={twMerge(
                        'w-[80px] text-sm font-[450] text-gray-700',
                        !cap && 'text-gray-300',
                        tdClassName,
                      )}
                    >
                      {toMathRound(cap)?.toLocaleString('en-US')}
                    </span>
                  </div>
                </td>
              )}
            </Fragment>
          ));
        })}
      </tr>
    </>
  );
};
